import { useMutation, useQuery } from "@apollo/client";
import { FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useState } from "react";

import { EXCEEDANCE_REFETCH, ExceedanceKey } from "./exceedanceKeys";
import { isCorrectionLanded, ManifestCorrectionRow } from "./manifestCorrectionDisplay";
import { ManifestCorrectionPicker } from "./ManifestCorrectionPicker";
import { LinkableSubmissionOption, SubmissionPicker } from "./SubmissionPicker";

const MARK_RESOLVED_MUTATION = gql(`
  mutation MarkGeneratorStatusExceedancesResolved(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
    $submissionId: ID
    $manifestDiscrepancyIds: [ID!]
  ) {
    markGeneratorStatusExceedancesResolved(
      exceedances: $exceedances
      note: $note
      submissionId: $submissionId
      manifestDiscrepancyIds: $manifestDiscrepancyIds
    ) {
      id
      status
    }
  }
`);

const LINKABLE_SUBMISSIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableSubmissions($facilityId: ID!, $search: String) {
    generatorStatusExceedanceLinkableSubmissions(facilityId: $facilityId, search: $search) {
      id
      title
      submissionType
      status
      dueDate
      createdAt
      facilityName
      assigneeName
    }
  }
`);

const LINKABLE_MANIFEST_CORRECTIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableManifestCorrections(
    $facilityId: ID!
    $source: String!
    $periodStart: Date!
    $search: String
  ) {
    generatorStatusExceedanceLinkableManifestCorrections(
      facilityId: $facilityId
      source: $source
      periodStart: $periodStart
      search: $search
    ) {
      id
      processedManifestId
      manifestTrackingNumber
      shippedDate
      wasteLineNumber
      field
      status
      resolutionPath
      eManifestValue
      vendorValue
      resolvedAt
      resolvedByName
    }
  }
`);

/** Which resolution the dialog is about to record. */
type ResolveMode = "acknowledge" | "link" | "correction";

const SAVE_TEXT: Record<ResolveMode, string> = {
  acknowledge: "Mark resolved",
  link: "Link submission",
  correction: "Resolve via correction",
};

const SUCCESS_MESSAGE: Record<ResolveMode, string> = {
  acknowledge: "Marked resolved.",
  link: "Linked to submission.",
  correction: "Resolved via manifest correction.",
};

/**
 * What the correction path is about to do. Citing only landed fixes resolves the
 * row outright; citing anything still in flight parks it until every fix lands —
 * the same ALL rule the server applies, so the button never promises the wrong
 * outcome.
 */
const CORRECTION_PARK_SAVE_TEXT = "Link corrections";
const CORRECTION_PARK_SUCCESS = "Waiting on the linked manifest corrections.";

function toResolveMode(value: string): ResolveMode {
  if (value === "link") return "link";
  if (value === "correction") return "correction";
  return "acknowledge";
}

type MarkResolvedDialogProps = {
  open: boolean;
  onClose: () => void;
  exceedanceKey: ExceedanceKey;
  facilityId: string;
  /** Builds the per-candidate link-outs to submissions and manifests. */
  tenantId: string;
  /**
   * Corrections the exceedance already cites. The correction picker opens with these
   * checked, so "Mark resolved" edits the cited set rather than starting blank —
   * re-resolving otherwise means rebuilding it from memory.
   */
  citedCorrectionIds?: string[];
  onResolved: () => void;
};

/**
 * "Mark resolved" dialog. Three paths:
 *  - Corrected elsewhere: acknowledge with a note, no submission.
 *  - Link to an existing submission: pick one of the facility's recent submissions
 *    (e.g. an in-progress notification for a consecutive-month exceedance, or an
 *    "Other"-typed memo to file). The exceedance then tracks that submission's
 *    status instead of being flatly "resolved".
 *  - Manifest correction: cite the corrections on manifests that shipped in the
 *    affected period. If every cited fix has landed, the exceedance resolves as
 *    MANIFEST_CORRECTION with them recorded as the fix; if any is still in flight
 *    the exceedance parks in PENDING and resolves once they all land.
 *
 * Both pickers are presentational: this component owns their queries so the
 * "selection the grid no longer shows" filtering stays derived from the rows in
 * one place, instead of an effect inside each child.
 */
export function MarkResolvedDialog({
  open,
  onClose,
  exceedanceKey,
  facilityId,
  tenantId,
  citedCorrectionIds,
  onResolved,
}: MarkResolvedDialogProps) {
  const [mode, setMode] = useState<ResolveMode>("acknowledge");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<LinkableSubmissionOption | null>(null);
  const [selectedCorrectionIds, setSelectedCorrectionIds] = useState<string[]>([]);
  // Server-side search: each picker returns only the newest candidates, so older
  // ones are reachable by narrowing rather than paging.
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const { data, loading: loadingOptions } = useQuery(LINKABLE_SUBMISSIONS_QUERY, {
    variables: { facilityId, search: trimmedSearch || undefined },
    skip: !open || mode !== "link",
    fetchPolicy: "cache-and-network",
  });

  const { data: correctionData, loading: loadingCorrections } = useQuery(
    LINKABLE_MANIFEST_CORRECTIONS_QUERY,
    {
      variables: {
        facilityId,
        source: exceedanceKey.source,
        periodStart: exceedanceKey.periodStart,
        search: trimmedSearch || undefined,
      },
      skip: !open || mode !== "correction",
      fetchPolicy: "cache-and-network",
    }
  );

  const [markResolved, { loading: submitting }] = useMutation(MARK_RESOLVED_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const alerts = useAlerts();

  const options: LinkableSubmissionOption[] =
    data?.generatorStatusExceedanceLinkableSubmissions ?? [];
  const corrections: ManifestCorrectionRow[] =
    correctionData?.generatorStatusExceedanceLinkableManifestCorrections ?? [];

  // Seeded on open rather than at mount: the drawer renders this dialog before its
  // detail query (and so the cited set) has resolved, so a mount-time initializer
  // would always seed empty. Keyed on `open` alone so editing the selection while
  // the dialog is up doesn't get overwritten.
  useEffect(() => {
    if (open) setSelectedCorrectionIds(citedCorrectionIds ?? []);
    // oxlint-disable-next-line exhaustive-deps
  }, [open]);

  // A search that filters the selected row out of the grid also clears the
  // selection — an invisible selection would otherwise still be submittable.
  useEffect(() => {
    if (selected && data && !options.some((o) => o.id === selected.id)) {
      setSelected(null);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [data]);

  // Derived, not stored, for the same reason — filtering at render keeps a pick
  // alive if the search that hid it is widened again.
  const visibleCorrections = corrections.filter((c) => selectedCorrectionIds.includes(c.id));
  const visibleCorrectionIds = visibleCorrections.map((c) => c.id);
  // Only the visible picks are submitted, so a selection the search (or the row
  // cap) has hidden would otherwise disappear without a word — including the
  // citations seeded on a re-resolve. Surfaced in the picker instead of silently
  // dropped. Suppressed mid-fetch, when every row is momentarily "hidden".
  const hiddenSelectedCount = loadingCorrections
    ? 0
    : selectedCorrectionIds.length - visibleCorrectionIds.length;
  // ALL, not ANY: each checked fix accounts for part of the excess, so one landing
  // is no reason to clear the row.
  const parksOnCorrections =
    visibleCorrections.length > 0 && !visibleCorrections.every(isCorrectionLanded);

  // Re-seeded rather than cleared: the dialog is mounted alongside the drawer, so a
  // second open must show the row's CURRENT citations, not the last edit's.
  const reset = () => {
    setMode("acknowledge");
    setNote("");
    setSelected(null);
    setSelectedCorrectionIds(citedCorrectionIds ?? []);
    setSearch("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleModeChange = (value: string) => {
    // Each mode searches a different catalog, so a carried-over search string
    // would silently filter the new picker to nothing.
    setSearch("");
    setMode(toResolveMode(value));
  };

  const handleSubmit = async () => {
    try {
      await markResolved({
        variables: {
          exceedances: [exceedanceKey],
          note: note.trim(),
          submissionId: mode === "link" ? selected?.id : undefined,
          manifestDiscrepancyIds: mode === "correction" ? visibleCorrectionIds : undefined,
        },
      });
      alerts.success(
        mode === "correction" && parksOnCorrections
          ? CORRECTION_PARK_SUCCESS
          : SUCCESS_MESSAGE[mode]
      );
      reset();
      onResolved();
    } catch (e) {
      alerts.error("Failed to mark resolved.", e);
    }
  };

  // A note is required: it's the audit record of why/how this was resolved.
  const nothingPicked =
    (mode === "link" && !selected) || (mode === "correction" && visibleCorrectionIds.length === 0);
  const submitDisabled = submitting || nothingPicked || !note.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 860, maxWidth: "95vw" } } }}
      title="Mark discrepancy resolved"
      description="Acknowledge this discrepancy, link it to an existing submission so its status tracks that filing, or cite the manifest corrections that fix it — resolving now if they have all landed, waiting on them if they haven't."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: submitting,
        loadingText: "Saving...",
        hideStartIcon: true,
        // Linking a submission — and citing a correction that hasn't landed — moves
        // the discrepancy to PENDING to track that thing's lifecycle; the other
        // paths resolve it outright. The button says which is about to happen.
        saveText:
          mode === "correction" && parksOnCorrections ? CORRECTION_PARK_SAVE_TEXT : SAVE_TEXT[mode],
        disabled: submitDisabled,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      <RadioGroup value={mode} onChange={(e) => handleModeChange(e.target.value)}>
        <FormControlLabel
          value="acknowledge"
          control={<Radio size="small" />}
          label="Corrected elsewhere — no submission needed"
        />
        <FormControlLabel
          value="link"
          control={<Radio size="small" />}
          label="Link to an existing submission"
        />
        <FormControlLabel
          value="correction"
          control={<Radio size="small" />}
          label="Manifest correction"
        />
      </RadioGroup>

      {mode === "link" && (
        <SubmissionPicker
          rows={options}
          loading={loadingOptions}
          search={search}
          onSearchChange={setSearch}
          searching={trimmedSearch.length > 0}
          selectedId={selected?.id ?? null}
          onSelect={(id) => setSelected(options.find((o) => o.id === id) ?? null)}
          tenantId={tenantId}
        />
      )}

      {mode === "correction" && (
        <ManifestCorrectionPicker
          rows={corrections}
          loading={loadingCorrections}
          search={search}
          onSearchChange={setSearch}
          searching={trimmedSearch.length > 0}
          selectedIds={visibleCorrectionIds}
          hiddenSelectedCount={hiddenSelectedCount}
          onSelectionChange={setSelectedCorrectionIds}
          tenantId={tenantId}
        />
      )}

      <TextField
        label="Note (required)"
        required
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Dialog>
  );
}
